// ============================================
// Status constant (mirrors backend STATUS_WON)
// ============================================
export const STATUS_WON = 'Шалгарсан';

// ============================================
// Format Compact — currency-free compact number in Mongolian units
// (KPI cards, chart axes). "127.3 мянга", "128.8 их наяд" — never K/M/B/T.
// ============================================
const MN_UNITS: [number, string][] = [
  [1e12, 'их наяд'],
  [1e9, 'тэрбум'],
  [1e6, 'сая'],
  [1e3, 'мянга'],
];

export const formatCompact = (num: number, digits = 1): string => {
  if (num == null || Number.isNaN(num)) return '0';
  const abs = Math.abs(num);
  for (const [size, unit] of MN_UNITS) {
    if (abs >= size) {
      // "3.0 мянга" → "3 мянга": a trailing .0 is noise on a KPI
      const v = (num / size).toFixed(digits).replace(/\.0+$/, '');
      return `${v} ${unit}`;
    }
  }
  return num.toLocaleString();
};

/**
 * Unit for a KPI tile, picked from the final value so it doesn't change while
 * the number animates. Below 10,000 there is no unit — the exact count
 * ("2,964") is short enough and more informative than "3 мянга".
 */
export function mnScale(num: number): { size: number; unit: string } {
  const abs = Math.abs(num || 0);
  if (abs < 1e4) return { size: 1, unit: '' };
  const hit = MN_UNITS.find(([size]) => abs >= size);
  return hit ? { size: hit[0], unit: hit[1] } : { size: 1, unit: '' };
}

/** Number part for a KPI in the given scale: "127.3", or "2,964" when unscaled. */
export function formatScaled(num: number, size: number): string {
  if (size === 1) return Math.round(num).toLocaleString();
  return (num / size).toFixed(1).replace(/\.0$/, '');
}

// ============================================
// Format Money — 6 оронтой уншихад тохиромжтой (T/B/M/K), том тоог T (их наяд) хүртэл
// ============================================
export const formatMoney = (num: number | null | undefined): string => {
  if (num == null || Number.isNaN(num)) return '₮0';
  if (num >= 1e12) return `₮${(num / 1e12).toFixed(2)} их наяд`;
  if (num >= 1e9) return `₮${(num / 1e9).toFixed(2)} тэрбум`;
  if (num >= 1e6) return `₮${(num / 1e6).toFixed(2)} сая`;
  if (num >= 1e3) return `₮${(num / 1e3).toFixed(1)} мянга`;
  return `₮${num.toLocaleString()}`;
};

// ============================================
// Format Number
// ============================================
export const formatNumber = (num: number | null | undefined): string => {
  if (num == null || Number.isNaN(num)) return '0';
  return num.toLocaleString();
};

// ============================================
// Calculate Win Rate
// ============================================
export const calculateWinRate = (wins: number, participations: number): number => {
  if (participations === 0) return 0;
  return (wins / participations) * 100;
};

// ============================================
// Graph link endpoint → string node id
// ============================================
export function getLinkId(
  link: { source: unknown; target: unknown },
  end: 'source' | 'target',
): string {
  const v = link[end];
  return typeof v === 'object' && v !== null ? (v as { id: string }).id : String(v);
}

// ============================================
// Parse selected year string to valid year number
// ============================================
export function parseValidYear(selectedYear: string | undefined): number | undefined {
  if (!selectedYear) return undefined;
  const y = parseInt(selectedYear, 10);
  if (isNaN(y) || y < 2000 || y > new Date().getFullYear()) return undefined;
  return y;
}

// ============================================
// Bidder risk score (mirrors backend compute_bidder_risk)
// risk = win_rate × log2(participations + 1) × 15, capped at 100
// ============================================
export function computeBidderRisk(wins: number, participations: number): number {
  if (participations <= 0) return 0;
  const winRate = wins / participations;
  const score = winRate * Math.log2(participations + 1) * 15;
  return Math.min(100, Math.round(score * 10) / 10);
}

// ============================================
// Org risk score (mirrors backend compute_org_risk)
// risk = concentration × log2(won + 1) × 15, capped at 100
// ============================================
export function computeOrgRisk(wonTenders: number, uniqueWinners: number): number {
  if (wonTenders <= 0 || uniqueWinners <= 0) return 0;
  const concentration = 1 - uniqueWinners / wonTenders;
  if (concentration <= 0) return 0;
  const volume = Math.log2(wonTenders + 1);
  return Math.min(100, Math.round(concentration * volume * 15 * 10) / 10);
}

// ============================================
// Risk level union type
// ============================================
export type RiskLevel = 'critical' | 'high' | 'medium' | 'low';

// ============================================
// Risk label with CSS class
// ============================================
export function riskLabel(score: number): { text: string; cls: RiskLevel } {
  if (score >= 75) return { text: 'Маш өндөр', cls: 'critical' };
  if (score >= 50) return { text: 'Өндөр', cls: 'high' };
  if (score >= 25) return { text: 'Дунд', cls: 'medium' };
  return { text: 'Бага', cls: 'low' };
}

// ============================================
// Shared pagination constants
// ============================================
export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

// ============================================
// Deduplicate tenders by tender_id (preserves first occurrence)
// ============================================
export function dedupeByTenderId<T extends { tender_id: number }>(list: T[]): T[] {
  const seen = new Set<number>();
  return list.filter((t) => {
    if (seen.has(t.tender_id)) return false;
    seen.add(t.tender_id);
    return true;
  });
}

// ============================================
// Build year trend from tender list
// ============================================
export function buildYearTrend<T extends { year?: number | null; winner_name?: string | null }>(
  tenders: T[],
): { year: number; tenders: number; won: number }[] {
  const map = new Map<number, { year: number; tenders: number; won: number }>();
  for (const t of tenders) {
    if (!t.year) continue;
    if (!map.has(t.year)) map.set(t.year, { year: t.year, tenders: 0, won: 0 });
    const entry = map.get(t.year)!;
    entry.tenders++;
    if (t.winner_name) entry.won++;
  }
  return Array.from(map.values()).sort((a, b) => a.year - b.year);
}

